import { createContext, useMemo, useReducer, useRef, useState } from 'react';

import { IMPERFECT_THEME_DOMAIN, MISFITS_THEME_DOMAIN } from 'constants/theme';
import { getBrandDomain } from 'utils';
import { getAnonymousId } from 'utils/globalUtils';

const GlobalContext = createContext(null);

const DEFAULT_FILTER_OPTIONS = {
    soldOut: false,
    new: false,
    onSale: false,
    bestSellers: false,
    lastChance: false,
};

function GlobalProvider({ children }) {
    const brandDomain = getBrandDomain();
    const urlParams = new URLSearchParams(window.location.search);
    const openCart = urlParams.get('cart') || false;
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const [cartOpen, setCartOpen] = useState((window.isNativeApp && openCart === 'true') ?? false);
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [productAdded, setProductAddedState] = useState(false);
    const [productAddedID, setProductAddedID] = useState(null);
    const [addCounter, setAddCounter] = useState(0);
    const [theme, setTheme] = useState(brandDomain ?? MISFITS_THEME_DOMAIN);
    const [alerts, setAlerts] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [splitKey, setSplitKey] = useState(localStorage.getItem('customerID') ?? getAnonymousId());

    const resetFilterOptions = () => {
        forceUpdate();
    };

    const urlFilters = urlParams.get('filters')?.split(',') || [];
    const filterOptions = urlFilters.reduce(
        (filterObject, filterItem) => ({
            ...filterObject,
            [filterItem]: true,
        }),
        DEFAULT_FILTER_OPTIONS
    );

    const setFilterOptions = (newFilterOptions) => {
        const combinedNewFilters = {
            ...filterOptions,
            ...newFilterOptions,
        };

        const appliedFilterArrayString = Object.keys(combinedNewFilters)
            .filter((filterKey) => combinedNewFilters[filterKey])
            .toString();

        if (appliedFilterArrayString.length > 0) {
            urlParams.set('filters', appliedFilterArrayString);
        } else {
            urlParams.delete('filters');
        }

        const urlParamsString = urlParams.toString();
        const newUrl = `${window.location.pathname}${urlParamsString.length > 0 ? '?' : ''}${urlParamsString}`;

        window.history.replaceState(null, '', newUrl);

        // This "forces" the component to re-render, updating the url params by
        // re-reading the url search value. Without this change, multiple filter
        // changes would be overwritten without a re-render, causing only the
        // most recently applied filter to be applied
        forceUpdate();
    };

    const productAddedTimer = useRef();

    const setProductAdded = () => {
        setProductAddedState(true);

        if (productAddedTimer.current) {
            clearTimeout(productAddedTimer.current);
        }

        productAddedTimer.current = setTimeout(() => {
            setProductAddedState(false);
        }, 3000);
    };

    const contextValue = useMemo(
        () => ({
            autocompleteOpen,
            cartOpen,
            filtersOpen,
            filterOptions,
            isImperfectTheme: theme === IMPERFECT_THEME_DOMAIN,
            isMisfitsTheme: theme === MISFITS_THEME_DOMAIN,
            productAdded,
            productAddedID,
            resetFilterOptions,
            searchQuery,
            splitKey,
            setSearchQuery,
            setAutocompleteOpen,
            setCartOpen,
            setFiltersOpen,
            setFilterOptions,
            setProductAdded,
            setProductAddedID,
            setSplitKey,
            setTheme,
            theme,
            alerts,
            setAlerts,
            addCounter,
            setAddCounter,
        }),
        [
            addCounter,
            alerts,
            autocompleteOpen,
            cartOpen,
            filtersOpen,
            filterOptions,
            productAdded,
            searchQuery,
            splitKey,
            theme,
        ]
    );

    return <GlobalContext.Provider value={contextValue}>{children}</GlobalContext.Provider>;
}

export { GlobalContext, GlobalProvider };
